import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin, ArrowRight, Music, Users, Sparkles, Mail, CheckCircle, ExternalLink, Newspaper } from "lucide-react";
import { GoFundMeHomeBanner } from "@/components/GoFundMe";

const Landing = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubscribed(true);
      toast.success("You're on the list!");
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="hero-bg min-h-screen flex items-center"
        style={{
          backgroundImage: `url('/assets/exterior.jpg')`
        }}
        data-testid="hero-section"
      >
        <div className="hero-content w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-tight mb-6 animate-fade-in opacity-0 delay-100">
              Step into the<br />
              <span className="text-electric">Electric Buzz</span><br />
              of the Night
            </h1>
            <p className="text-white/70 text-lg sm:text-xl leading-relaxed mb-8 max-w-xl animate-fade-in opacity-0 delay-200">
              It's an adventure waiting to happen! Let your senses be your guide. 
              Experience the heartpounding beats, dazzling lights, and unforgettable moments 
              at The Brownstone Jazz Club.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in opacity-0 delay-300">
              <Link to="/private-events">
                <Button 
                  className="bg-electric text-obsidian hover:bg-electric-light px-8 py-6 text-base font-medium"
                  data-testid="hero-cta-private"
                >
                  Book Private Event
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:border-electric hover:text-electric px-8 py-6 text-base"
                  data-testid="hero-cta-contact"
                >
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-obsidian" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-4xl sm:text-5xl text-white mb-6">
                Experience the Soul of<br />
                <span className="text-electric">Uptown Minneapolis</span>
              </h2>
              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                <div className="border border-white/10 rounded-lg p-6 bg-obsidian-light">
                  <Music className="w-8 h-8 text-electric mb-3" />
                  <h3 className="font-display text-lg text-white mb-2">Live Music & Entertainment</h3>
                  <p className="text-white/50 text-sm">
                    The Brownstone Jazz Club is proud to bring a platform for music and the arts to bring people together. Experience amazing local talent and featured touring artists nightly with us!
                  </p>
                </div>
                <div className="border border-white/10 rounded-lg p-6 bg-obsidian-light">
                  <Users className="w-8 h-8 text-electric mb-3" />
                  <h3 className="font-display text-lg text-white mb-2">Strengthening Our Community</h3>
                  <p className="text-white/50 text-sm">
                    Our beautiful and welcoming space is perfect for those looking to host their next event requiring a playful yet elegant setting. Contact us today to schedule a tour.
                  </p>
                </div>
                <div className="border border-white/10 rounded-lg p-6 bg-obsidian-light">
                  <Sparkles className="w-8 h-8 text-electric mb-3" />
                  <h3 className="font-display text-lg text-white mb-2">Perfect for a Night Out</h3>
                  <p className="text-white/50 text-sm">
                    Let The Brownstone Jazz Club set the vibe for your next date night! The shimmering lights and smooth music are great for casual and intimate experiences.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/assets/hero-sax.jpg"
                alt="Live jazz performance at BrownStone"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-velvet p-6 rounded-lg border border-amber/30">
                <p className="font-display text-white text-lg italic">
                  "Where the music breathes"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GoFundMe Banner - Above Article */}
      <section className="py-8 bg-obsidian">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GoFundMeHomeBanner />
        </div>
      </section>

      {/* Featured Article - Minnesota Spokesman-Recorder */}
      <section className="py-24 bg-obsidian-light" data-testid="featured-article-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-electric font-display italic mb-2">In The News</p>
              <h2 className="font-display text-4xl text-white">Featured In Minnesota Spokesman-Recorder</h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Article Image */}
            <div className="relative group">
              <a 
                href="https://spokesman-recorder.com/2025/12/26/brownstone-jazz-club-minneapolis/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block"
              >
                <img 
                  src="https://i0.wp.com/spokesman-recorder.com/wp-content/uploads/2025/12/Brown1.jpg?resize=771%2C514&ssl=1"
                  alt="The Brownstone Jazz Club Minneapolis"
                  className="rounded-lg shadow-2xl w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg" />
                <div className="absolute bottom-6 left-6 right-6">
                  <span className="inline-flex items-center gap-2 bg-electric/90 text-obsidian px-3 py-1 rounded-full text-sm font-medium mb-3">
                    <Newspaper className="w-4 h-4" />
                    Minnesota Spokesman-Recorder
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl text-white">
                    The Brownstone Jazz Club Opens in Uptown With a Focus on Community
                  </h3>
                </div>
              </a>
            </div>

            {/* Article Content */}
            <div className="space-y-6">
              <p className="text-white/80 text-lg leading-relaxed">
                The Brownstone Jazz Club in Minneapolis is a new jazz venue created by longtime musician <span className="text-electric font-medium">Johnnie Brown</span> to expand access and opportunity for local artists.
              </p>
              
              <p className="text-white/70 leading-relaxed">
                Located in Uptown, the club aims to be a community-centered space that supports Black artists, fosters growth, and offers a mature, welcoming environment for live music.
              </p>

              <p className="text-white/70 leading-relaxed">
                Brown's vision is to build a venue the community can be proud of — a destination where everyone feels welcome, and artists at all stages of their careers have space to share and develop their craft.
              </p>

              <div className="pt-4">
                <a 
                  href="https://spokesman-recorder.com/2025/12/26/brownstone-jazz-club-minneapolis/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-electric text-obsidian hover:bg-electric-light px-6 py-3 rounded-lg font-medium transition-colors"
                  data-testid="read-article-btn"
                >
                  Read Full Article
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 bg-obsidian" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-electric font-display italic mb-2">Find Us</p>
              <h2 className="font-display text-4xl text-white mb-6">
                Perfect for a<br />Night Out
              </h2>
              <p className="text-white/70 leading-relaxed mb-8">
                Let The Brownstone Jazz Club set the vibe for your next date night! 
                Our bar catering option will have your drinks ready to help you relax and take the edge off 
                of meeting someone new. The shimmering lights and smooth music are great for casual and intimate experiences.
              </p>
              <div className="flex items-start gap-4 p-6 border border-white/10 rounded-lg bg-obsidian-light">
                <MapPin className="w-6 h-6 text-electric flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg text-white mb-1">Our Location</h3>
                  <p className="text-white/60">
                    2756 Hennepin Ave<br />
                    Minneapolis, MN 55408
                  </p>
                  <p className="text-white/40 text-sm mt-1">Corner of 28th & Hennepin</p>
                  <p className="text-electric text-sm mt-2">Uptown Minneapolis</p>
                </div>
              </div>
              <div className="flex gap-6 mt-6">
                <a 
                  href="https://www.instagram.com/brownstonejazzmn/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/60 hover:text-electric transition-colors"
                >
                  <span className="text-sm">@brownstonejazzmn</span>
                </a>
                <a 
                  href="https://www.facebook.com/share/16oboa3auE/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/60 hover:text-electric transition-colors"
                >
                  <span className="text-sm">Facebook</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/assets/treble-sculpture.jpg"
                alt="BrownStone Jazz Club treble clef sculpture"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-24 relative overflow-hidden"
        style={{
          backgroundImage: `url('/assets/performance-1.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-obsidian/90" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-white mb-6">
            Ready to Experience<br />
            <span className="text-electric">the Magic?</span>
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Join our mailing list and never miss a show. 
            Book the venue for your next special occasion.
          </p>
          
          {/* Mailing List Signup */}
          <div className="max-w-md mx-auto mb-10">
            {subscribed ? (
              <div className="flex items-center justify-center gap-2 text-electric py-3" data-testid="subscribe-success">
                <CheckCircle className="w-5 h-5" />
                <span>You're on the list!</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2" data-testid="subscribe-form">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-obsidian/50 border-white/20 text-white placeholder:text-white/40 focus:border-electric"
                  data-testid="subscribe-email"
                />
                <Button 
                  type="submit"
                  disabled={subscribing}
                  className="bg-electric text-obsidian hover:bg-electric-light px-6 whitespace-nowrap"
                  data-testid="subscribe-btn"
                >
                  {subscribing ? (
                    <span className="w-4 h-4 border-2 border-obsidian border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Join
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/private-events">
              <Button 
                className="bg-electric text-obsidian hover:bg-electric-light px-8 py-6 text-base font-medium"
                data-testid="cta-private-events"
              >
                Book Private Event
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-white/30 text-white hover:border-electric hover:text-electric px-8 py-6 text-base"
                data-testid="cta-contact"
              >
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
